import React from "react";
import { ArrowRight } from "lucide-react";
import useInView from "../hooks/useInView";
import { useRole } from "../context/RoleContext";

const Thesis = () => {
  const { thesis } = useRole();
  const [w1, w2, w3] = thesis.words;
  const [ref, inView] = useInView();

  return (
    <section
      id="thesis"
      data-testid="thesis-section"
      className="section-pad border-t border-white/5 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(800px 380px at 80% 0%, rgba(236, 240, 241,0.12), transparent 60%), radial-gradient(700px 380px at 12% 100%, rgba(189, 195, 199,0.08), transparent 60%)"
        }}
      />
      {/* ambient floating dots */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <span className="absolute top-[18%] left-[12%] w-2 h-2 rounded-full blob-c" style={{ background: "#FF8A5B", boxShadow: "0 0 14px #FF8A5B" }} />
        <span className="absolute top-[60%] right-[10%] w-2.5 h-2.5 rounded-full blob-a" style={{ background: "#FFD2A6", boxShadow: "0 0 14px #FFD2A6" }} />
        <span className="absolute top-[80%] left-[40%] w-1.5 h-1.5 rounded-full blob-b" style={{ background: "#FF8A5B", boxShadow: "0 0 14px #FF8A5B" }} />
      </div>

      <div className="relative container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="eyebrow mb-3">{thesis.eyebrow}</div>
            <h2 data-testid="thesis-heading" className="display text-[44px] md:text-[72px] leading-[0.98] text-[#FFF3E9]">
              {w1}{" "}
              <span style={{ color: "#FF8A5B" }}>{w2}</span>{" "}
              <span className="serif-italic" style={{ color: "#FFD2A6" }}>
                {w3}
              </span>
            </h2>
          </div>
          <p className="mono text-[11px] tracking-[0.18em] uppercase text-[#8B7B6E] md:text-right shrink-0">
            {thesis.meta}
          </p>
        </div>

        {/* 3-step flow */}
        <div ref={ref} className="relative grid md:grid-cols-3 gap-6 md:gap-8">
          {/* dashed connector line behind cards (md+) */}
          <div
            aria-hidden
            className="hidden md:block absolute top-[88px] left-[8%] right-[8%] h-0"
            style={{
              borderTop: "2px dashed rgba(255,255,255,0.18)"
            }}
          />

          {thesis.steps.map((step, idx) => (
            <div
              key={step.id}
              data-testid={`thesis-step-${step.id}`}
              className="relative group transition-all duration-700"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(34px)",
                transitionDelay: `${idx * 140}ms`
              }}
            >
              {/* arrow between cards */}
              {idx < thesis.steps.length - 1 && (
                <div
                  aria-hidden
                  className="hidden md:flex absolute -right-5 top-[78px] z-10 w-10 h-10 items-center justify-center rounded-full"
                  style={{ background: step.color, color: "#121826" }}
                >
                  <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                </div>
              )}

              <div
                className="relative rounded-3xl p-6 md:p-7 border border-white/10 transition-all duration-300 group-hover:-translate-y-1.5"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0) 100%)"
                }}
              >
                {/* Number sticker */}
                <div className="flex items-start justify-between mb-6">
                  <span
                    className="display text-[44px] md:text-[56px] leading-none"
                    style={{ color: step.color }}
                  >
                    {step.kicker}
                  </span>
                  <span
                    className="sticker tilt-r-soft"
                    style={{ background: step.color, color: "#121826" }}
                  >
                    {step.tag}
                  </span>
                </div>

                <h3 className="display text-[24px] md:text-[28px] leading-tight text-[#FFF3E9] mb-4">
                  {step.title}
                </h3>

                <ul className="space-y-2.5">
                  {step.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-2.5 text-[14px] text-[#FFD2A6]"
                    >
                      <span
                        className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: step.color }}
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Thesis;
